import { ApiError } from '@/lib/api/error';
import { HTTP_STATUS } from '@/lib/api/http-status';
import type { InvitationDetail } from '../-type';

/** `network` は経路側の失敗（fetch 失敗・本文なしの 200・JSON として読めない本文）。 */
export type InvitationLoadFailureKind = 'network' | 'server';

/** リトライしても復旧しなかった取得失敗。種別はエラー画面の案内文に使う。 */
export class InvitationLoadError extends Error {
  readonly kind: InvitationLoadFailureKind;
  readonly statusCode?: number;
  constructor(kind: InvitationLoadFailureKind, options: {
    statusCode?: number;
    cause?: unknown;
  } = {}) {
    super(`Failed to load invitation (${kind})`);
    this.name = 'InvitationLoadError';
    this.kind = kind;
    this.statusCode = options.statusCode;
    this.cause = options.cause;
  }
}

/** openapi-fetch の戻り値のうち、リトライ判定に使う部分。 */
export type InvitationFetchResult = {
  data?: InvitationDetail;
  error?: unknown;
  response: Pick<Response, 'status'>;
};

/** 初回 + 2 リトライ。 */
const INVITATION_RETRY_DELAYS_MS = [300, 900];
type LoadOptions = {
  retryDelaysMs?: readonly number[];
  sleep?: (ms: number) => Promise<void>;
};
type AttemptOutcome = {
  type: 'success';
  data: InvitationDetail;
} | {
  type: 'rejected';
  error: ApiError;
} | {
  type: 'transient';
  error: InvitationLoadError;
};

/** 招待が無効・受諾済み・期限切れ。再試行しても結果は変わらない。 */
const isInvitationRejected = (statusCode: number) => statusCode === HTTP_STATUS.BAD_REQUEST || statusCode === HTTP_STATUS.NOT_FOUND;

/**
 * リトライしてよい throw は fetch 自体の失敗（TypeError）と、JSON として読めない
 * 本文（SyntaxError）だけ。認証ミドルウェアが 401 / 403 で投げるエラーには
 * 再ログイン・権限ダイアログといった専用の導線があるため、握り潰さず伝播させる。
 */
const isTransportError = (error: unknown) => error instanceof TypeError || error instanceof SyntaxError;

/** 非 JSON / 空ボディでも実応答の status を失わせない（`handleForbidden` と同じ意図）。 */
const toApiError = (body: unknown, statusCode: number) => new ApiError(typeof body === 'object' && body !== null ? {
  ...body,
  statusCode
} : {
  statusCode
});
const defaultSleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));
const runAttempt = async (fetchInvitation: () => Promise<InvitationFetchResult>): Promise<AttemptOutcome> => {
  let res: InvitationFetchResult;
  try {
    res = await fetchInvitation();
  } catch (cause) {
    if (!isTransportError(cause)) {
      throw cause;
    }
    return {
      type: 'transient',
      error: new InvitationLoadError('network', {
        cause
      })
    };
  }
  if (res.data) {
    return {
      type: 'success',
      data: res.data
    };
  }

  // 本文なしの応答では openapi-fetch が error も undefined で返すため、
  // エラー本文ではなく実応答の status で判定する。
  const {
    status
  } = res.response;
  if (status >= HTTP_STATUS.BAD_REQUEST) {
    const error = toApiError(res.error, status);
    if (isInvitationRejected(status)) {
      return {
        type: 'rejected',
        error
      };
    }
    return {
      type: 'transient',
      error: new InvitationLoadError('server', {
        statusCode: status,
        cause: error
      })
    };
  }

  // 2xx なのに本文が無い（配信経路での切断・プロキシ介入）。招待の状態とは無関係。
  return {
    type: 'transient',
    error: new InvitationLoadError('network', {
      statusCode: status
    })
  };
};

/**
 * 招待情報を取得する。一過性の通信失敗は指数バックオフでリトライし、
 * 招待そのものが無効な場合（400 / 404）はリトライせず理由を投げる。
 */
export const loadInvitationDetail = async (fetchInvitation: () => Promise<InvitationFetchResult>, options: LoadOptions = {}): Promise<InvitationDetail> => {
  const retryDelaysMs = options.retryDelaysMs ?? INVITATION_RETRY_DELAYS_MS;
  const sleep = options.sleep ?? defaultSleep;
  let lastError = new InvitationLoadError('network');
  for (let attempt = 0; attempt <= retryDelaysMs.length; attempt++) {
    if (attempt > 0) {
      await sleep(retryDelaysMs[attempt - 1] ?? 0);
    }
    const outcome = await runAttempt(fetchInvitation);
    if (outcome.type === 'success') {
      return outcome.data;
    }
    if (outcome.type === 'rejected') {
      throw outcome.error;
    }
    lastError = outcome.error;
  }
  throw lastError;
};